import type { Role } from '../types/identity'

/**
 * Account records as administration sees them.
 *
 * Deliberately narrow: an administrator needs to identify an account, see what
 * it is authorised to do and act on its standing. Nothing here carries notes,
 * diary entries, messages, wellbeing records or balances — those are not
 * administrative data, and leaving them out of the record is the only reliable
 * way to keep them out of the console.
 */
export type AccountState = 'active' | 'pending_verification' | 'restricted' | 'suspended' | 'closed'

export interface AccountFlag {
  id: string
  label: string
  detail: string
  /** Automated signals are observations for a human, never a decision. */
  automated: boolean
  raised: string
}

export interface AccountAction {
  id: string
  action: string
  actor: string
  at: string
  reason: string
}

export interface AdminUser {
  /** Authoritative key. The username is a changeable label and is never a key. */
  id: string
  username: string
  displayName: string
  emailMasked: string
  state: AccountState
  stateReason: string | null
  roles: Role[]
  campus: string | null
  institution: string | null
  emailVerified: boolean
  academicVerification: 'none' | 'email' | 'document' | 'institution'
  createdAt: string
  lastSeen: string
  openReports: number
  flags: AccountFlag[]
  history: AccountAction[]
  /** Present only for accounts that submitted identity evidence. */
  identityDocuments: { kind: string; submitted: string; decision: string }[]
}

export const adminUsers: AdminUser[] = [
  {
    id: 'CAMP-8FD21A44',
    username: 'amarachi',
    displayName: 'Amarachi Okeke',
    emailMasked: 'a•••••••••bi@campuzon.com',
    state: 'active',
    stateReason: null,
    roles: ['student'],
    campus: 'Akoka',
    institution: 'University of Lagos',
    emailVerified: true,
    academicVerification: 'institution',
    createdAt: '14 Feb 2026',
    lastSeen: '2 hours ago',
    openReports: 0,
    flags: [],
    history: [
      {
        id: 'act-1',
        action: 'Academic identity verified',
        actor: 'University of Lagos — Registry',
        at: '2 Mar 2026',
        reason: 'Enrolment confirmed against the institutional register.',
      },
    ],
    identityDocuments: [],
  },
  {
    id: 'CAMP-31B90C7E',
    username: 'brandreach.ng',
    displayName: 'BrandReach Nigeria',
    emailMasked: 'c•••••••@brandreach.ng',
    state: 'restricted',
    stateReason: 'Repeated unsolicited messaging to students outside an active application.',
    roles: ['employer'],
    campus: null,
    institution: null,
    emailVerified: true,
    academicVerification: 'none',
    createdAt: '3 Jan 2026',
    lastSeen: '1 day ago',
    openReports: 12,
    flags: [
      {
        id: 'flag-1',
        label: 'High message volume to unconnected students',
        detail: '340 first-contact messages in 7 days, against a median of 11 for employers.',
        automated: true,
        raised: '9 Aug 2026',
      },
      {
        id: 'flag-2',
        label: 'Twelve student reports',
        detail: 'Reports cite unsolicited contact. No financial loss has been reported.',
        automated: false,
        raised: '10 Aug 2026',
      },
    ],
    history: [
      {
        id: 'act-2',
        action: 'Restricted — messaging paused',
        actor: 'M. Adeyinka · Trust & Safety',
        at: '11 Aug 2026',
        reason:
          'Twelve upheld reports of unsolicited contact. Listings remain live; messaging is paused pending response.',
      },
    ],
    identityDocuments: [
      { kind: 'Company registration', submitted: '3 Jan 2026', decision: 'Accepted' },
    ],
  },
  {
    id: 'CAMP-77A0E512',
    username: 'danielu',
    displayName: 'Daniel Uche',
    emailMasked: 'd•••••u@futa.edu.ng',
    state: 'active',
    stateReason: null,
    roles: ['student', 'provider'],
    campus: 'Main Campus',
    institution: 'Federal University of Technology, Akure',
    emailVerified: true,
    academicVerification: 'email',
    createdAt: '22 Nov 2025',
    lastSeen: '20 minutes ago',
    openReports: 1,
    flags: [
      {
        id: 'flag-3',
        label: 'One open dispute',
        detail: 'A buyer disputed delivery on order ORD-40233. Escrow is holding the value.',
        automated: false,
        raised: '12 Aug 2026',
      },
    ],
    history: [],
    identityDocuments: [],
  },
  {
    id: 'CAMP-5C4417B9',
    username: 'iwaya.pm',
    displayName: 'Iwaya Property Managers',
    emailMasked: 'o•••••@iwayapm.ng',
    state: 'pending_verification',
    stateReason: 'Owner identity evidence submitted and awaiting review.',
    roles: ['property'],
    campus: 'Akoka',
    institution: null,
    emailVerified: false,
    academicVerification: 'none',
    createdAt: '10 Aug 2026',
    lastSeen: '4 hours ago',
    openReports: 0,
    flags: [],
    history: [],
    identityDocuments: [
      { kind: 'Owner identity', submitted: '10 Aug 2026', decision: 'Awaiting review' },
      { kind: 'Property document', submitted: '11 Aug 2026', decision: 'Awaiting review' },
    ],
  },
  {
    id: 'CAMP-9E33D680',
    username: 'quickcash.deals',
    displayName: 'Quick Cash Deals',
    emailMasked: 'q•••••••@mail.com',
    state: 'suspended',
    stateReason:
      'Requested payment outside Campuzon for an inspection that no listing record supports.',
    roles: ['provider'],
    campus: null,
    institution: null,
    emailVerified: true,
    academicVerification: 'none',
    createdAt: '28 Jul 2026',
    lastSeen: '5 days ago',
    openReports: 7,
    flags: [
      {
        id: 'flag-4',
        label: 'Off-platform payment requests',
        detail: 'Seven students reported being asked to transfer an inspection fee directly.',
        automated: false,
        raised: '6 Aug 2026',
      },
    ],
    history: [
      {
        id: 'act-3',
        action: 'Suspended',
        actor: 'K. Balogun · Risk',
        at: '7 Aug 2026',
        reason:
          'Seven consistent reports of off-platform payment requests. Records preserved; appeal open.',
      },
    ],
    identityDocuments: [],
  },
  {
    id: 'CAMP-2AB6F901',
    username: 'gracen',
    displayName: 'Grace Nwosu',
    emailMasked: 'g••••n@unilag.edu.ng',
    state: 'active',
    stateReason: null,
    roles: ['student', 'representative'],
    campus: 'Akoka',
    institution: 'University of Lagos',
    emailVerified: true,
    academicVerification: 'document',
    createdAt: '8 Sep 2025',
    lastSeen: 'Yesterday',
    openReports: 0,
    flags: [],
    history: [
      {
        id: 'act-4',
        action: 'Representative role granted',
        actor: 'Network operations',
        at: '4 Jun 2026',
        reason: 'Application approved, onboarding completed, activated on Akoka.',
      },
    ],
    identityDocuments: [
      { kind: 'Student ID', submitted: '1 Feb 2026', decision: 'Accepted' },
    ],
  },
]

export const STATE_LABEL: Record<AccountState, string> = {
  active: 'Active',
  pending_verification: 'Pending verification',
  restricted: 'Restricted',
  suspended: 'Suspended',
  closed: 'Closed',
}

export const STATE_MEANING: Record<AccountState, string> = {
  active: 'Full use of everything this account\u2019s roles authorise.',
  pending_verification: 'Signed in and limited. Cannot transact until verification completes.',
  restricted:
    'Specific capabilities paused. The person keeps their records and can still respond and appeal.',
  suspended:
    'Cannot sign in. Every record is preserved, including their ledger history and anything owed to them.',
  closed:
    'Closed at the person\u2019s request. Ledger entries are anonymised and retained; nothing is deleted.',
}
